import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash, Plus, Minus, ArrowLeft, ShoppingCart, Warning, WhatsappLogo } from "@phosphor-icons/react";
import { money, consultPriceUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { items, payItems, consultItems, removeItem, setQuantity, total } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-28 text-center" data-testid="empty-cart">
        <div className="w-16 h-16 mx-auto border border-white/15 flex items-center justify-center text-gray-500 mb-6">
          <ShoppingCart size={28} />
        </div>
        <h1 className="font-display text-5xl">Tu carrito está vacío</h1>
        <p className="text-gray-400 mt-3">Todavía no has agregado ninguna cerveza.</p>
        <Link to="/catalogo" className="inline-flex items-center gap-2 mt-8 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold uppercase tracking-wide px-7 py-4 transition-colors">
          Ir al catálogo
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-12">
      <Link to="/catalogo" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D97736] text-sm uppercase tracking-widest mb-8 transition-colors">
        <ArrowLeft size={16} /> Seguir comprando
      </Link>
      <h1 className="font-display text-5xl md:text-6xl mb-10">Carrito</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 h-fit space-y-px bg-white/10 border border-white/10">
          {items.map((it) => (
            <div key={it.key || it.id} className="bg-[#141414] p-4 flex gap-4 items-center" data-testid={`cart-item-${it.key || it.id}`}>
              <img src={it.image_url} alt={it.name} className="w-20 h-24 object-cover border border-white/10" />
              <div className="flex-1 min-w-0">
                <div className="overline">{it.style}</div>
                <h3 className="font-display text-2xl leading-tight">{it.name}</h3>
                <div className="text-[#D97736] font-semibold mt-1">{it.consult ? "Precio a consultar" : money(it.price)}</div>
              </div>
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQuantity(it.key || it.id, it.quantity - 1)} className="w-9 h-9 flex items-center justify-center hover:text-[#D97736]" data-testid={`decrease-${it.key || it.id}`}>
                  <Minus size={14} />
                </button>
                <span className="w-9 text-center text-sm" data-testid={`qty-${it.key || it.id}`}>{it.quantity}</span>
                <button onClick={() => setQuantity(it.key || it.id, it.quantity + 1)} className="w-9 h-9 flex items-center justify-center hover:text-[#D97736]" data-testid={`increase-${it.key || it.id}`}>
                  <Plus size={14} />
                </button>
              </div>
              <div className="w-24 text-right font-display text-2xl hidden sm:block">{it.consult ? "—" : money(it.price * it.quantity)}</div>
              <button onClick={() => removeItem(it.key || it.id)} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-red-400 transition-colors" data-testid={`remove-${it.key || it.id}`}>
                <Trash size={18} />
              </button>
            </div>
          ))}
        </div>

        <div className="lg:col-span-1 h-fit bg-[#171717] border border-white/10 p-6 lg:sticky lg:top-28" data-testid="cart-summary">
          <div className="overline mb-4">Resumen</div>
          <div className="flex justify-between text-sm text-gray-400 py-2 border-b border-white/10">
            <span>Subtotal</span><span>{money(total)}</span>
          </div>
          <div className="flex justify-between text-sm text-gray-400 py-2 border-b border-white/10">
            <span>Envío</span><span>Se calcula al pagar</span>
          </div>
          <div className="flex justify-between items-end py-5">
            <span className="overline">Total</span>
            <span className="font-display text-4xl text-[#D97736]">{money(total)}</span>
          </div>
          {payItems.length > 0 && (
            <button
              onClick={() => nav("/checkout")}
              className="w-full bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold uppercase tracking-wide py-4 transition-colors"
              data-testid="checkout-btn"
            >
              Proceder al pago
            </button>
          )}
          {consultItems.length > 0 && (
            <div className="mt-4" data-testid="cart-consult-box">
              <p className="text-xs text-gray-400 mb-2">Las cervezas importadas tienen precio a consultar y no se cobran aquí.</p>
              <a
                href={consultPriceUrl(consultItems.map((i) => `${i.quantity}x ${i.name}`))}
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-black font-semibold uppercase tracking-wide py-4 text-sm transition-colors"
                data-testid="cart-consult-whatsapp"
              >
                <WhatsappLogo size={20} weight="fill" /> Consultar importadas por WhatsApp
              </a>
            </div>
          )}
          {items.some((i) => !i.size && !/^(Té|Pack)/.test(i.name)) && (
          <p className="mt-4 flex items-start gap-2 text-xs text-[#F5A623] leading-relaxed" data-testid="cart-stock-reminder">
            <Warning size={15} weight="fill" className="shrink-0 mt-0.5" />
            <span>Cervezas artesanales de baja producción: confirma la disponibilidad por WhatsApp antes de pagar, se agotan rápido.</span>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
